import { reactive } from "vue";
import axios from "axios";
import { getError } from "#/utils/notification";

const enum AccountRoutes {
  ACCOUNT_CHANGE_EMAIL_INPUT_EMAIL = "/account/change-email/input-email",
  ACCOUNT_CHANGE_EMAIL_CANCEL = "/account/change-email/cancel",
  ACCOUNT_CHANGE_EMAIL_UNLINK_EMAIL_FROM_OTHER_ACCOUNT = "/account/change-email/unlink",
  ACCOUNT_CHANGE_EMAIL_RESEND_VERIFICATION_CODE = "/account/change-email/resend-code",
  ACCOUNT_CHANGE_EMAIL_INPUT_VERIFICATION_CODE = "/account/change-email/input-code",

  ACCOUNT_CHANGE_PHONE_INPUT_PHONE = "/account/change-phone/input-phone",
  ACCOUNT_CHANGE_PHONE_CANCEL = "/account/change-phone/cancel",
  ACCOUNT_CHANGE_PHONE_UNLINK_EMAIL_FROM_OTHER_ACCOUNT = "/account/change-phone/unlink",
  ACCOUNT_CHANGE_PHONE_RESEND_VERIFICATION_CODE = "/account/change-phone/resend-code",
  ACCOUNT_CHANGE_PHONE_INPUT_VERIFICATION_CODE = "/account/change-phone/input-code",

  ACCOUNT_AJAX_CHANGE_PASSWORD = "/account/change/password",
}

export const enum DuplicateEmailResponseStatus {
  DuplicateApplicant = 2,
  DuplicateEmployer = 3,
  Success = 4,
}

export const enum DuplicatePhoneResponseStatus {
  Success = 3,
  Duplicate = 2,
}

interface ChangePassword {
  old: string;
  new: string;
  repeat: string;
}

const useAccount = () => {
  const state = reactive({
    isLoading: false,
  });

  const changeApplicantEmail = async (email: string) => {
    try {
      state.isLoading = true;

      const { data } = await axios.post<{ step: number }>(
        `${AccountRoutes.ACCOUNT_CHANGE_EMAIL_INPUT_EMAIL}?email=${email}`
      );

      return data.step;
    } catch (e: any) {
      throw getError(e);
    } finally {
      state.isLoading = false;
    }
  };

  const approveVerificationCode = async (code: string) => {
    try {
      state.isLoading = true;

      const { data } = await axios.post<{ step: number }>(
        AccountRoutes.ACCOUNT_CHANGE_EMAIL_INPUT_VERIFICATION_CODE,
        null,
        { params: { code: code } }
      );

      return data.step;
    } catch (e: any) {
      throw getError(e);
    } finally {
      state.isLoading = false;
    }
  };

  const unlinkEmail = async () => {
    try {
      state.isLoading = true;

      const { data } = await axios.post<{ step: number }>(
        AccountRoutes.ACCOUNT_CHANGE_EMAIL_UNLINK_EMAIL_FROM_OTHER_ACCOUNT
      );

      return data.step;
    } catch (e: any) {
      throw getError(e);
    } finally {
      state.isLoading = false;
    }
  };

  const resendEmailCode = async () => {
    try {
      state.isLoading = true;
      const { data } = await axios.post(
        AccountRoutes.ACCOUNT_CHANGE_EMAIL_RESEND_VERIFICATION_CODE
      );

      return data.step;
    } catch (e: any) {
      throw getError(e);
    } finally {
      state.isLoading = false;
    }
  };

  const cancelChangeEmail = async () => {
    try {
      state.isLoading = true;
      const { data } = await axios.get(
        AccountRoutes.ACCOUNT_CHANGE_EMAIL_CANCEL
      );

      return data.step;
    } catch (e: any) {
      throw getError(e);
    } finally {
      state.isLoading = false;
    }
  };

  const phone = {
    async checkDuplicate(phone: string) {
      try {
        state.isLoading = true;

        const query = new URLSearchParams({ phone });

        const { data } = await axios.post<{ step: number }>(
          `${AccountRoutes.ACCOUNT_CHANGE_PHONE_INPUT_PHONE}?${query.toString()}`
        );
        return data.step;
      } catch (e: any) {
        throw getError(e);
      } finally {
        state.isLoading = false;
      }
    },

    async clearSession() {
      try {
        state.isLoading = true;

        const { data } = await axios.post<{ step: number }>(
          AccountRoutes.ACCOUNT_CHANGE_PHONE_CANCEL
        );
        return data.step;
      } catch (e: any) {
        throw getError(e);
      } finally {
        state.isLoading = false;
      }
    },

    async unlink() {
      try {
        state.isLoading = true;

        const { data } = await axios.post<{ step: number }>(
          AccountRoutes.ACCOUNT_CHANGE_PHONE_UNLINK_EMAIL_FROM_OTHER_ACCOUNT
        );
        return data.step;
      } catch (e: any) {
        throw getError(e);
      } finally {
        state.isLoading = false;
      }
    },

    async resendCode() {
      try {
        state.isLoading = true;

        const { data } = await axios.post<{ step: number }>(
          AccountRoutes.ACCOUNT_CHANGE_PHONE_RESEND_VERIFICATION_CODE
        );
        return data.step;
      } catch (e: any) {
        throw getError(e);
      } finally {
        state.isLoading = false;
      }
    },

    async verificationCode(code: string) {
      try {
        state.isLoading = true;

        const { data } = await axios.post<{ step: number }>(
          `${AccountRoutes.ACCOUNT_CHANGE_PHONE_INPUT_VERIFICATION_CODE}?code=${code}`
        );
        return data.step;
      } catch (e: any) {
        throw getError(e);
      } finally {
        state.isLoading = false;
      }
    },
  };

  const password = {
    form: reactive<ChangePassword>({
      old: "",
      new: "",
      repeat: "",
    }),

    async changePassword(params: ChangePassword) {
      try {
        state.isLoading = true;

        const form = new FormData();
        form.append("oldPassword", params.old);
        form.append("newPassword", params.new);

        const { data } = await axios.post(
          `${AccountRoutes.ACCOUNT_AJAX_CHANGE_PASSWORD}`,
          form
        );

        return data.step;
      } catch (e: any) {
        throw getError(e);
      } finally {
        state.isLoading = false;
      }
    },
  };

  return {
    state,
    phone,
    password,
    changeApplicantEmail,
    cancelChangeEmail,
    unlinkEmail,
    resendEmailCode,
    approveVerificationCode,
  };
};

export { useAccount };
